import { useMsal } from '@azure/msal-react';
import { Button, Checkbox, Field, Input, makeStyles, Spinner, Text } from '@fluentui/react-components';
import { ArrowDownloadFilled } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { getFileIcon } from '../../common/file-icon/get-file-icon';
import { useStores } from '../../common/hooks';
import { ProgramDocument, RoleName } from '../../models';
import { getFileNameFromUrl } from '../../shared/functions';
import './ProgramDocumentCard.scss';
import { FlexBox } from './flex-box';

export type CardTheme = 'GREEN' | 'BROWN' | 'TEAL' | 'NAVY' | 'PURPLE' | 'RED';

const useStyles = makeStyles({
  checkboxLabel: {
    color: '#FFFFFF'
  },
  checkboxInput: {
    ':not(:checked)+div': {
      border: '1px solid #FFFFFF',
    },
  }
});

export const getCardThemeTypeByIndex = (index: number): CardTheme => {
  const totalThemeVariance = 6;
  const varianceNumber = index % totalThemeVariance;
  if (varianceNumber === 0) return 'GREEN';
  if (varianceNumber === 1) return 'BROWN';
  if (varianceNumber === 2) return 'TEAL';
  if (varianceNumber === 3) return 'NAVY';
  if (varianceNumber === 4) return 'PURPLE';
  return 'RED';
};

interface ProgramDocumentCardProps {
  programDocument: ProgramDocument;
  cardTheme?: CardTheme;
  editingMode?: {
    onRemoveButtonClicked: (programDocument: ProgramDocument) => void;
    onEditButtonClicked: (programDocument: ProgramDocument) => void;
  }
  isShowWinnerResource?: boolean;
}

const ProgramDocumentCardImpl: React.FC<ProgramDocumentCardProps> = (props) => {
  const {
    programDocument,
    editingMode,
    cardTheme = 'GREEN',
    isShowWinnerResource,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    userStore,
  } = useStores();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const classes = useStyles();

  const handleRemoveButtonClicked = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    editingMode?.onRemoveButtonClicked(programDocument);
  };

  const handleEditButtonClicked = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    editingMode?.onEditButtonClicked(programDocument);
  };

  const fileNameWithExtension = getFileNameFromUrl(programDocument.fileLocationUrl);

  const handleDownloadFile = async (): Promise<void> => {
    try {
      setIsDownloading(true);
      const ticketId = await apiStore.createProgramDocumentDownloadTicket(instance, programDocument.programDocumentId);
      const downloadUrl = `/api/program-documents/download?ticketId=${ticketId}`;
      window.location.href = downloadUrl;
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsDownloading(false);
    }
  };

  const getThemeClassName = (theme: CardTheme) => {
    if (theme === 'GREEN') return 'card-green';
    if (theme === 'BROWN') return 'card-brown';
    if (theme === 'TEAL') return 'card-teal';
    if (theme === 'NAVY') return 'card-navy';
    if (theme === 'PURPLE') return 'card-purple';
    return 'card-red';
  };

  return (
    <FlexBox
      direction='column'
      content='space-between'
      isFullWidth
      className={`program-document-card ${getThemeClassName(cardTheme)}`}
    >
      <FlexBox
        content='space-between'
        className='program-document-card__summary'
      >
        <FlexBox
          direction='column'
          content='space-between'
          className='summary-title'
        >
          <Text
            size={500}
            weight='semibold'
          >
            {programDocument.displayName}
          </Text>
          {
            programDocument.documentGroupTypeCode === 'winner-resource' &&
            <FlexBox
              direction='column'
              className='winner-checkboxes'
            >
              <Checkbox
                id={`winner-${programDocument.programDocumentId}`}
                readOnly
                size='large'
                checked={programDocument.winnerRole === 'Winner' || programDocument.winnerRole === 'Both'}
                input={{
                  className: classes.checkboxInput,
                }}
                label={{
                  className: classes.checkboxLabel,
                  children: 'Winner',
                }}
              />
              <Checkbox
                id={`finalist-${programDocument.programDocumentId}`}
                readOnly
                size='large'
                checked={programDocument.winnerRole === 'Finalist' || programDocument.winnerRole === 'Both'}
                input={{
                  className: classes.checkboxInput,
                }}
                label={{
                  className: classes.checkboxLabel,
                  children: 'Finalist',
                }}
              />
            </FlexBox>
          }
        </FlexBox>
        <Text
          size={200}
          className='summary-description'
        >
          {programDocument.description}
        </Text>
      </FlexBox>
      <FlexBox
        direction='column'
        className='program-document-card__resource'
      >
        <FlexBox
          content='space-between'
          aligns='center'
        >
          <FlexBox
            className='resource-file'
            aligns='center'
          >
            <img
              src={getFileIcon(programDocument.fileLocationUrl)}
              width={20}
              height={20}
              alt='file icon'
            />
            {fileNameWithExtension && (
              <>
                <Text
                  size={200}
                  weight='semibold'
                >
                  {decodeURI(fileNameWithExtension)}
                </Text>
                <Button
                  appearance='subtle'
                  onClick={() => handleDownloadFile()}
                  disabled={isDownloading}
                  title='Download'
                  icon={<ArrowDownloadFilled />}
                >
                  {isDownloading && <Spinner size='small' />}
                </Button>
              </>
            )}
          </FlexBox>
          {
            userStore.isAuthorized([RoleName.Admin]) && !!editingMode && (
              <FlexBox className='buttons-wrapper'>
                <Button
                  appearance='secondary'
                  onClick={(event) => handleRemoveButtonClicked(event)}
                >
                  Remove
                </Button>
                <Button
                  appearance='primary'
                  onClick={(event) => handleEditButtonClicked(event)}
                >
                  Edit
                </Button>
              </FlexBox>
            )
          }
        </FlexBox>
        {isShowWinnerResource && (
          <>
            <Field
              label='Role'
            >
              <Input
                appearance='filled-darker'
                type='text'
                readOnly
                value={programDocument.winnerRole || '-'}
              />
            </Field>
            <Field
              label='Type of award'
            >
              <Input
                appearance='filled-darker'
                type='text'
                readOnly
                value={programDocument.awardType || '-'}
              />
            </Field>
          </>
        )}
      </FlexBox>
    </FlexBox>
  );
};

export const ProgramDocumentCard = observer(ProgramDocumentCardImpl);
